import { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { getRememberMe, saveRememberMe, clearRememberMe } from '../../utils/rememberMe';
import { loginRequest } from '../../config/authConfig';

/**
 * AutoLogin component handles automatic sign-in when "Remember Me" is active
 * It checks if the user has enabled remember me and if the saved account
 * matches one of the currently signed-in MSAL accounts
 */
const AutoLogin = ({ children }) => {
    const { instance, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    useEffect(() => {
        const attemptAutoLogin = async () => {
            // Don't auto-login if already authenticated
            if (isAuthenticated) {
                setIsCheckingAuth(false);
                return;
            }

            const rememberMeData = getRememberMe();
            
            // No remember me data or expired
            if (!rememberMeData) {
                setIsCheckingAuth(false);
                return;
            }

            // Check if the saved username matches any MSAL account
            const matchingAccount = accounts.find(
                account => account.username === rememberMeData.username
            );

            if (matchingAccount) {
                try {
                    // Attempt silent login with the matching account
                    const response = await instance.ssoSilent({
                        ...loginRequest,
                        account: matchingAccount,
                        loginHint: matchingAccount.username
                    });

                    // Refresh the remember me timestamp on successful auto-login
                    if (response && response.account) {
                        saveRememberMe(response.account.username);
                    }
                } catch (error) {
                    console.log('Auto-login failed, user will need to sign in manually:', error);
                    // If auto-login fails, clear the remember me data
                    // This could indicate the session is no longer valid
                    clearRememberMe();
                }
            } else {
                // No matching MSAL account found - the user was likely logged out
                // Clear the remember me data since it's no longer valid
                console.log('No matching MSAL account found - clearing remember me data');
                clearRememberMe();
            }
            
            setIsCheckingAuth(false);
        };

        // Small delay to ensure MSAL is fully initialized
        const timer = setTimeout(() => {
            attemptAutoLogin();
        }, 100);

        return () => clearTimeout(timer);
    }, [instance, accounts, isAuthenticated]);

    // Show children immediately - auto-login happens in background
    // This prevents blocking the UI
    return children;
};

export default AutoLogin;
